import { DialogTitle, IconButton, Paper, Typography } from '@material-ui/core';
import Axios from 'axios';
import CommonAlert from 'components/CommonAlert/CommonAlert';
import CloseIconNavy from 'images/icons/signals/closesAndCancels/closeTransparent.svg';
import { useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import Environment from 'utils/environments';
import {
  StockExtractDialogContainer,
  StockExtractDialogContent
} from '../../styles';
import DateFilter from './components/DateFilter';
import ProductsSelector from './components/ProductsSelector';
import StockExtractTable from './components/StockExtractTable';
import moment from 'moment/moment';
import LabelPrintWriteOffDialog from '../LabelPrintWriteOffDialog';
import ConfirmModal from 'components/Dialogs/ConfirmModal/ConfirmModal';
import LabelDetailsDialog from '../LabelDetailsDialog';
import PrintQuantityDialog from '../PrintQuantityDialog';

function PaperComponent(props) {
  return (
    <Draggable
      handle="#draggableLabelPrintingStockExtractsDialog"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} style={{ ...props?.style, borderRadius: '8px' }} />
    </Draggable>
  );
}

const environment = Environment(process.env.REACT_APP_ENV);

const last7Start = () => moment().subtract(7, 'days').format('YYYY-MM-DD');
const today = () => moment().format('YYYY-MM-DD');

function StockExtractDialog({
  open,
  onClose = () => {},
  initialProduct,
  onSuccess = () => {},
  printLabel,
  setModalSettings
}) {
  const { id: paramsID } = useParams();

  const [filters, setFilters] = useState({
    startAt: null,
    endAt: null,
    isLabelCleared: true
  });

  const [product, setProduct] = useState(initialProduct ?? {});
  const productId = product?.id ?? null;

  const lastReqId = useRef(0);
  const initializedRef = useRef(false);

  const onlyDate = (d) => {
    if (!d) return null;
    if (typeof d === 'string') return d.slice(0, 10);
    return moment(d).format('YYYY-MM-DD');
  };

  const pickValidRange = (p) => {
    const startSrc = p?.firstLabelValidAt ?? p?.labelsStartAt ?? null;
    const endSrc = p?.lastLabelValidAt ?? p?.labelsEndAt ?? startSrc;
    const isEod = p?.validAtType === 'END_OF_DAY';
    const norm = (d) => (d ? onlyDate(d) : null);
    const normEod = (d) =>
      d
        ? moment(onlyDate(d), 'YYYY-MM-DD')
            .subtract(1, 'day')
            .format('YYYY-MM-DD')
        : null;
    return {
      start: isEod ? normEod(startSrc) : norm(startSrc),
      end: isEod ? normEod(endSrc) : norm(endSrc)
    };
  };

  useEffect(() => {
    setProduct(initialProduct ?? {});
  }, [initialProduct]);

  useEffect(() => {
    if (!productId || initializedRef.current) return;
    const { start, end } = pickValidRange(product);
    if (start && end) {
      setFilters((prev) => ({ ...prev, startAt: start, endAt: end }));
    } else {
      setFilters((prev) => ({
        ...prev,
        startAt: last7Start(),
        endAt: today()
      }));
    }
    initializedRef.current = true;
  }, [
    productId,
    product?.firstLabelValidAt,
    product?.lastLabelValidAt,
    product?.labelsStartAt,
    product?.labelsEndAt,
    product?.validAtType
  ]);

  const [extract, setExtract] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [internalModalSettings, setInternalModalSettings] = useState([
    { id: 1, name: 'labelPrintWriteOffDialog', open: false, labelItem: null },
    { id: 2, name: 'confirmDialog', open: false, title: '', content: '' },
    { id: 3, name: 'printQuantityDialog', open: false, labelItem: null },
    { id: 4, name: 'labelDetailsDialog', open: false, labelItem: null }
  ]);

  useEffect(() => {
    if (filters.startAt && filters.endAt) {
      const start = moment(filters.startAt, 'YYYY-MM-DD', true);
      const end = moment(filters.endAt, 'YYYY-MM-DD', true);
      if (start.isValid() && end.isValid() && start.isAfter(end)) {
        setFilters((prev) => ({
          ...prev,
          startAt: prev.endAt,
          endAt: prev.startAt
        }));
      }
    }
  }, [filters.startAt, filters.endAt]);

  const getExtracts = async () => {
    const reqId = ++lastReqId.current;
    setLoading(true);
    try {
      const startMoment = filters.startAt
        ? moment(filters.startAt, 'YYYY-MM-DD', true)
        : null;
      const endMoment = filters.endAt
        ? moment(filters.endAt, 'YYYY-MM-DD', true)
        : null;

      let startAt = filters.startAt ?? last7Start();
      let endAt = filters.endAt ?? today();

      if (
        startMoment &&
        endMoment &&
        startMoment.isValid() &&
        endMoment.isValid() &&
        startMoment.isAfter(endMoment)
      ) {
        startAt = endMoment.format('YYYY-MM-DD');
        endAt = startMoment.format('YYYY-MM-DD');
      }

      if (product?.validAtType === 'END_OF_DAY') {
        startAt = moment(startAt, 'YYYY-MM-DD')
          .add(1, 'day')
          .format('YYYY-MM-DD');
        endAt = moment(endAt, 'YYYY-MM-DD').add(1, 'day').format('YYYY-MM-DD');
      }

      const res = await Axios.get(
        `${environment.catalog}/printer/products/${productId}/labels`,
        {
          params: {
            restaurantId: paramsID,
            startAt,
            endAt,
            isLabelCleared: filters.isLabelCleared || null
          }
        }
      );

      if (reqId === lastReqId.current) {
        setExtract(res?.data ?? []);
      }
    } catch (error) {
      if (reqId === lastReqId.current) {
        setMessage(
          error?.response?.data?.message ||
            'Erro ao carregar histórico do estoque'
        );
      }
    } finally {
      if (reqId === lastReqId.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!productId) return;
    if (!filters.startAt || !filters.endAt) return;
    getExtracts();
  }, [productId, filters.startAt, filters.endAt, filters.isLabelCleared]);

  const clearLabel = async (label, numberOfLabels) => {
    const currentId =
      label?.batchId && Number(label?.notClearedCount) > 1
        ? label?.batchId
        : label?.id;

    try {
      const response = await Axios.put(
        `${environment.catalog}/printer/products/labels/${currentId}`,
        {
          isLabelCleared: false,
          quantity: Number(numberOfLabels)
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      setMessage({
        description: 'Etiqueta atualizada com sucesso',
        status: 'success'
      });

      setInternalModalSettings((prev) =>
        prev.map((modal) => {
          if (modal.name === 'labelPrintWriteOffDialog') {
            return { ...modal, open: false, labelItem: null };
          }
          if (modal.name === 'confirmDialog') {
            return {
              ...modal,
              open: false,
              product,
              title: '',
              content: '',
              onConfirm: null,
              onClose: null
            };
          }
          return { ...modal };
        })
      );
      onSuccess();

      getExtracts();
      return response.data;
    } catch (error) {
      setMessage({
        description: 'Erro ao atualizar etiqueta',
        status: 'error'
      });
      throw error;
    }
  };

  return (
    <StockExtractDialogContainer
      className="defaultModal"
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperComponent={PaperComponent}
    >
      <DialogTitle
        className="cursorMove pRelative textAlignCenter"
        id="draggableLabelPrintingStockExtractsDialog"
        style={{ backgroundColor: 'inherit', height: '40px' }}
      >
        <Typography
          style={{ textAlign: 'center', color: '#FFF', fontWeight: 600 }}
        >
          HISTÓRICO DE ETIQUETAS
        </Typography>
      </DialogTitle>

      <StockExtractDialogContent>
        <DateFilter filters={filters} setFilters={setFilters} />
        <ProductsSelector product={product} setProduct={setProduct} />

        <StockExtractTable
          product={product}
          extract={extract}
          isLoading={loading}
          printLabel={printLabel}
          clearLabel={clearLabel}
          setModalSettings={setModalSettings}
          setInternalModalSettings={setInternalModalSettings}
          onRowClick={(labelItem) => {
            setInternalModalSettings((prev) =>
              prev.map((m) =>
                m.name === 'labelDetailsDialog'
                  ? { ...m, open: true, labelItem }
                  : m
              )
            );
          }}
        />
      </StockExtractDialogContent>

      {internalModalSettings.find((modal) => modal.name === 'confirmDialog')
        ?.open && (
        <ConfirmModal
          open={
            internalModalSettings.find(
              (modal) => modal.name === 'confirmDialog'
            )?.open
          }
          title={
            internalModalSettings.find(
              (modal) => modal.name === 'confirmDialog'
            )?.title
          }
          content={
            <Typography className="bold">
              {
                internalModalSettings.find(
                  (modal) => modal.name === 'confirmDialog'
                )?.content
              }
            </Typography>
          }
          onConfirm={
            internalModalSettings.find(
              (modal) => modal.name === 'confirmDialog'
            )?.onConfirm
          }
          onClose={() =>
            internalModalSettings
              .find((modal) => modal.name === 'confirmDialog')
              ?.onClose?.()
          }
          cancelText="Não"
          confirmText="Sim"
        />
      )}

      {internalModalSettings.find(
        (modal) => modal.name === 'labelPrintWriteOffDialog'
      )?.open && (
        <LabelPrintWriteOffDialog
          open={
            internalModalSettings.find(
              (modal) => modal.name === 'labelPrintWriteOffDialog'
            )?.open
          }
          onClose={() => {
            setInternalModalSettings(
              internalModalSettings.map((modal) =>
                modal.name === 'labelPrintWriteOffDialog'
                  ? { ...modal, open: false, labelItem: null }
                  : modal
              )
            );
          }}
          onSubmit={(label, numberOfLabels) => {
            clearLabel(label, numberOfLabels);
          }}
          labelItem={
            internalModalSettings.find(
              (modal) => modal.name === 'labelPrintWriteOffDialog'
            )?.labelItem
          }
        />
      )}

      {internalModalSettings.find((m) => m.name === 'printQuantityDialog')
        ?.open && (
        <PrintQuantityDialog
          open={
            internalModalSettings.find((m) => m.name === 'printQuantityDialog')
              ?.open
          }
          labelItem={
            internalModalSettings.find((m) => m.name === 'printQuantityDialog')
              ?.labelItem
          }
          onClose={() =>
            setInternalModalSettings((prev) =>
              prev.map((m) =>
                m.name === 'printQuantityDialog'
                  ? { ...m, open: false, labelItem: null }
                  : m
              )
            )
          }
          onSubmit={(label, qty) => {
            printLabel?.({ ...label, numberOfLabels: Number(qty) }, true);
            setInternalModalSettings((prev) =>
              prev.map((m) =>
                m.name === 'printQuantityDialog'
                  ? { ...m, open: false, labelItem: null }
                  : m
              )
            );
          }}
        />
      )}

      {internalModalSettings.find((m) => m.name === 'labelDetailsDialog')
        ?.open && (
        <LabelDetailsDialog
          open={
            internalModalSettings.find((m) => m.name === 'labelDetailsDialog')
              ?.open
          }
          labelItem={
            internalModalSettings.find((m) => m.name === 'labelDetailsDialog')
              ?.labelItem
          }
          onClose={() =>
            setInternalModalSettings((prev) =>
              prev.map((m) =>
                m.name === 'labelDetailsDialog'
                  ? { ...m, open: false, labelItem: null }
                  : m
              )
            )
          }
        />
      )}

      <CommonAlert
        open={message?.description}
        onClose={() => setMessage({ description: '', status: '' })}
        severity={message?.status}
        indexMessage={message?.description}
        messagePersonal={message?.description}
      />
    </StockExtractDialogContainer>
  );
}

function mapStateToProps(state, ownProps) {
  return { ...ownProps };
}

function mapDispatchToProps() {}

export default connect(mapStateToProps, mapDispatchToProps)(StockExtractDialog);
